import React, { useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient, getErrorMessage } from '../../packages/api-client';
import { PageHeader, Button, Input, Card, Badge, Dialog, StateView, Tabs } from '../../packages/ui';
import { usePageTitle } from '../../hooks/usePageTitle';
import './RolesPage.css';

export interface Role { id: string; name: string; description: string; created_at: string; }
export interface Permission {
    id: string;
    resource: string;
    action: string;
    description: string;
    /** Role names that currently hold this permission (from GET /permissions). */
    roles: string[];
}

/** Friendly labels for resource groups in the matrix and the catalogue. */
const RESOURCE_LABELS: Record<string, string> = {
    navigation: 'Navigation (menu visibility)',
    users: 'Users',
    roles: 'Groups & roles',
    permissions: 'Permissions',
    audit: 'Audit',
    projects: 'Projects',
};

export function resourceLabel(resource: string): string {
    return RESOURCE_LABELS[resource] ?? resource.charAt(0).toUpperCase() + resource.slice(1);
}

/** Group by resource, navigation first (menu visibility is the common edit), then A–Z. */
export function groupByResource(permissions: Permission[]): [string, Permission[]][] {
    const byResource = new Map<string, Permission[]>();
    for (const p of permissions) {
        if (!byResource.has(p.resource)) byResource.set(p.resource, []);
        byResource.get(p.resource)!.push(p);
    }
    return [...byResource.entries()].sort(([a], [b]) => {
        if (a === 'navigation') return -1;
        if (b === 'navigation') return 1;
        return a.localeCompare(b);
    });
}

export const fetchPermissions = async () =>
    (await apiClient.get<{ data: Permission[] | null }>('/permissions')).data.data ?? [];

export const BREADCRUMB = [{ label: 'Platform console' }, { label: 'Groups & Permissions' }];

/** Policy editing and read-only inspection are separate pages (handoff §5). */
export function GroupsPermissionsTabs() {
    return (
        <Tabs label="Groups & Permissions sections" items={[
            { to: '/admin/roles', label: 'Global role catalogue', end: true },
            { to: '/admin/permissions', label: 'Permission catalogue', end: true },
        ]} />
    );
}

/**
 * The only roles a tenant member can hold (users.AllowedTenantRoles; D4).
 * Deleting either would cascade user_roles in every tenant; the backend
 * refuses it (409) and the page never offers it.
 */
const BUILT_IN = ['admin', 'user'];
/** permissions.CasbinService.Enforce returns true for `admin` before reading policy. */
const ALWAYS_ALLOWED = 'admin';

type Pending =
    | { kind: 'create'; name: string; description: string }
    | { kind: 'delete'; role: Role }
    | { kind: 'grant' | 'revoke'; role: Role; perm: Permission };

export function RolesPage() {
    usePageTitle('Global role catalogue');
    const nameId = useId();
    const descId = useId();

    const [newName, setNewName] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [formError, setFormError] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [pending, setPending] = useState<Pending | null>(null);
    const [busy, setBusy] = useState(false);
    const [mutationError, setMutationError] = useState('');

    const rolesQ = useQuery({
        queryKey: ['roles'],
        queryFn: async () => (await apiClient.get<{ data: Role[] | null }>('/roles')).data.data ?? [],
    });
    const permsQ = useQuery({ queryKey: ['permissions'], queryFn: fetchPermissions });

    const roles = rolesQ.data ?? [];
    const grouped = useMemo(() => groupByResource(permsQ.data ?? []), [permsQ.data]);
    const selected = roles.find((r) => r.id === selectedId)
        ?? roles.find((r) => r.name === 'user') ?? roles[0];

    const closeDialog = () => { if (!busy) { setPending(null); setMutationError(''); } };

    const startCreate = (e: React.FormEvent) => {
        e.preventDefault();
        const name = newName.trim();
        if (!name) { setFormError('Group name is required'); return; }
        setFormError('');
        setPending({ kind: 'create', name, description: newDesc.trim() });
    };

    const confirm = async () => {
        if (!pending) return;
        setBusy(true);
        setMutationError('');
        try {
            switch (pending.kind) {
                case 'create':
                    await apiClient.post('/roles', { name: pending.name, description: pending.description });
                    setNewName('');
                    setNewDesc('');
                    break;
                case 'delete':
                    await apiClient.delete(`/roles/${pending.role.id}`);
                    if (selectedId === pending.role.id) setSelectedId(null);
                    break;
                case 'grant':
                    await apiClient.post(`/roles/${pending.role.id}/permissions/${pending.perm.id}`);
                    break;
                case 'revoke':
                    await apiClient.delete(`/roles/${pending.role.id}/permissions/${pending.perm.id}`);
                    break;
            }
            setPending(null);
            await Promise.all([rolesQ.refetch(), permsQ.refetch()]);
        } catch (err) {
            setMutationError(getErrorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const loadError = rolesQ.error ?? permsQ.error;

    return (
        <div className="gp-page">
            <PageHeader
                breadcrumb={BREADCRUMB}
                title="Global role catalogue"
                subtitle="Shared role definitions and their permissions. There is one catalogue for the whole platform."
            />
            <GroupsPermissionsTabs />

            <div role="note" className="gp-scope">
                <p className="gp-scope-title"><Badge variant="warning">Global scope</Badge> Every change on this page applies to every tenant.</p>
                <ul>
                    <li>Tenant admins can assign only <strong>admin</strong> or <strong>user</strong> to their members, one role each.</li>
                    <li>Custom groups are platform-only definitions: the role assignment API accepts only admin and user, so a custom group cannot be given to a tenant member.</li>
                    <li><strong>admin</strong> is always allowed by the server; its permission list does not restrict it. Changing <strong>user</strong> changes what every tenant's members can do and see.</li>
                    <li>The superadmin designation is a separate flag, not a role here.</li>
                </ul>
            </div>

            {loadError ? (
                <StateView state="error" message={getErrorMessage(loadError)}
                    onRetry={() => { rolesQ.refetch(); permsQ.refetch(); }}
                    retrying={rolesQ.isFetching || permsQ.isFetching} />
            ) : rolesQ.isLoading || permsQ.isLoading ? (
                <StateView state="loading" label="Loading roles and permissions" />
            ) : (
                <div className="gp-layout">
                    <div className="gp-side">
                        <Card>
                            <h2 className="gp-h2">Roles</h2>
                            {roles.length === 0 ? (
                                <StateView state="empty" title="No roles defined" />
                            ) : (
                                <ul className="gp-role-list">
                                    {roles.map((r) => {
                                        const builtIn = BUILT_IN.includes(r.name);
                                        return (
                                            <li key={r.id} className="gp-role-row">
                                                <button type="button" className="gp-role" aria-pressed={r.id === selected?.id}
                                                    onClick={() => setSelectedId(r.id)}>
                                                    <strong>{r.name}</strong>{' '}
                                                    <small>{builtIn ? 'Built-in tenant role' : 'Custom global group'}</small>
                                                </button>
                                                {!builtIn && (
                                                    <Button variant="ghost" size="sm" aria-label={`Delete ${r.name}`}
                                                        onClick={() => setPending({ kind: 'delete', role: r })}>
                                                        Delete
                                                    </Button>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </Card>

                        <Card>
                            <form onSubmit={startCreate} noValidate>
                                <h2 className="gp-h2">New global group</h2>
                                <p className="gp-muted">A new group starts with no permissions.</p>
                                <Input id={nameId} label="Name" placeholder="e.g. Auditors" value={newName}
                                    onChange={(e) => setNewName(e.target.value)} error={formError || undefined} />
                                <Input id={descId} label="Description" placeholder="What this group is for" value={newDesc}
                                    onChange={(e) => setNewDesc(e.target.value)} />
                                <Button type="submit">Create group</Button>
                            </form>
                        </Card>
                    </div>

                    {selected && (
                        <Matrix role={selected} grouped={grouped}
                            onToggle={(perm, grant) => setPending({ kind: grant ? 'grant' : 'revoke', role: selected, perm })} />
                    )}
                </div>
            )}

            <Dialog
                open={pending !== null}
                onClose={closeDialog}
                closeOnBackdrop={false}
                title={pending ? dialogTitle(pending) : ''}
                footer={pending && (
                    <>
                        <Button variant="secondary" onClick={closeDialog} disabled={busy}>Cancel</Button>
                        <Button variant={pending.kind === 'delete' || pending.kind === 'revoke' ? 'danger' : 'primary'}
                            onClick={confirm} isLoading={busy}>
                            {confirmLabel(pending)}
                        </Button>
                    </>
                )}
            >
                {pending && <p className="gp-dialog-body">{dialogBody(pending)} This applies to all tenants.</p>}
                {mutationError && <p role="alert" className="gp-error">{mutationError}</p>}
            </Dialog>
        </div>
    );
}

function Matrix({ role, grouped, onToggle }: {
    role: Role;
    grouped: [string, Permission[]][];
    onToggle: (perm: Permission, grant: boolean) => void;
}) {
    const headingId = useId();
    const readOnly = role.name === ALWAYS_ALLOWED;
    return (
        <section className="gp-matrix card" aria-labelledby={headingId}>
            <h2 id={headingId} className="gp-h2">Permissions for {role.name}</h2>
            <p className="gp-muted">
                {readOnly
                    ? 'admin is always allowed by the server, so these grants cannot restrict it and are shown read-only.'
                    : 'Check to grant, uncheck to revoke. Each change asks for confirmation. Navigation permissions control which menus the role sees.'}
            </p>
            {grouped.length === 0 ? (
                <StateView state="empty" title="No permissions in the catalogue" />
            ) : grouped.map(([resource, perms]) => (
                <div key={resource} role="group" aria-label={resourceLabel(resource)} className="gp-resource">
                    <h3 className="gp-h3">{resourceLabel(resource)}</h3>
                    <ul className="gp-perm-list">
                        {perms.map((p) => {
                            const code = `${p.resource}:${p.action}`;
                            const granted = p.roles?.includes(role.name) ?? false;
                            return (
                                <li key={p.id}>
                                    <label className="gp-perm">
                                        <input type="checkbox" checked={granted} disabled={readOnly}
                                            aria-label={`${code} for ${role.name}`}
                                            onChange={(e) => onToggle(p, e.target.checked)} />
                                        <span>
                                            <span className="gp-perm-desc">{p.description || p.action}</span>
                                            <code className="gp-code">{code}</code>
                                        </span>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ))}
        </section>
    );
}

function dialogTitle(p: Pending): string {
    switch (p.kind) {
        case 'create': return `Create global group "${p.name}"?`;
        case 'delete': return `Delete global group "${p.role.name}"?`;
        case 'grant': return `Grant ${p.perm.resource}:${p.perm.action} to ${p.role.name}?`;
        case 'revoke': return `Revoke ${p.perm.resource}:${p.perm.action} from ${p.role.name}?`;
    }
}

function dialogBody(p: Pending): string {
    switch (p.kind) {
        case 'create': return 'The group is added to the shared catalogue with no permissions.';
        case 'delete': return 'The group, its permission grants and any existing memberships are removed.';
        case 'grant': return `Every holder of ${p.role.name}, in every tenant, gains this permission.`;
        case 'revoke': return `Every holder of ${p.role.name}, in every tenant, loses this permission.`;
    }
}

function confirmLabel(p: Pending): string {
    return { create: 'Create', delete: 'Delete group', grant: 'Grant', revoke: 'Revoke' }[p.kind];
}
